import {
  menuCoachmarkStepAtom,
  menuCoachmarkWelcomeOpenAtom,
} from '@/atoms/menuCoachmarkAtoms';
import { ROUTE_TO_SIDEBAR_CONTENT } from '@/atoms/sidebarPanelAtoms';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightBody,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightImage,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { useTranslations } from '@heidi/runtime/i18n';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, type ReactNode } from 'react';

/**
 * Vertical offset (px) from the top of the SidebarPanel wrapper where the
 * virtual anchor sits. The arrow will point at this height; the card appears
 * slightly above it (controlled by alignOffset).
 */
const ANCHOR_TOP_PX = 172;

interface MenuCoachmarkWelcomeProps {
  children?: ReactNode;
}

/**
 * Welcome spotlight for the menu changeboarding flow.
 * Self-contained: reads atom state directly, provides its own handlers.
 * Shows a preview of the new pinnable session list with a "Show me" CTA.
 *
 * Uses a small virtual anchor positioned near the top of the SidebarPanel
 * so the arrow renders at the correct height (matching the Figma design)
 * instead of defaulting to the center of the full-height panel.
 *
 * Figma: node 1345:76282
 */
export const MenuCoachmarkWelcome = ({ children }: MenuCoachmarkWelcomeProps) => {
  const { t } = useTranslations();
  const isOpen = useAtomValue(menuCoachmarkWelcomeOpenAtom);
  const setWelcomeOpen = useSetAtom(menuCoachmarkWelcomeOpenAtom);
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();
  const router = useRouter();

  const isOnSidebarRoute = ROUTE_TO_SIDEBAR_CONTENT.some((entry) =>
    router.pathname.startsWith(entry.pathPrefix)
  );

  const handleShowMe = useCallback(() => {
    setWelcomeOpen(false);

    if (isOnSidebarRoute) {
      setStep(1);
    } else {
      // Navigate to a sidebar route so tour steps have the right context.
      void router
        .push('/scribe')
        .then((success) => {
          if (success !== false) {
            setStep(1);
          }
        })
        .catch(() => {
          // Navigation failed; re-open welcome so the user isn't left stranded
          setWelcomeOpen(true);
        });
    }
  }, [setWelcomeOpen, setStep, isOnSidebarRoute, router]);

  const handleDismiss = useCallback(() => {
    markModalAsViewed({
      modal_id: ModalId.MENU_COACHMARK_TOUR,
      increment_count: true,
    });
  }, [markModalAsViewed]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setWelcomeOpen(false);
      }
    },
    [setWelcomeOpen]
  );

  return (
    <div className="relative w-fit">
      {children}
      {isOpen && (
        <Spotlight open onOpenChange={handleOpenChange}>
          <SpotlightAnchor asChild>
            <div
              className="pointer-events-none absolute left-0 h-px w-full"
              style={{ top: ANCHOR_TOP_PX }}
              aria-hidden="true"
            />
          </SpotlightAnchor>
          <SpotlightContent
            side="right"
            sideOffset={8}
            align="start"
            alignOffset={-20}
            arrowPadding={8}
            data-testid="menu-coachmark-welcome"
          >
            <SpotlightArrow width={14} height={9} />
            <SpotlightHeader>
              <SpotlightTitle>{t('menuCoachmark.welcomeTitle')}</SpotlightTitle>
              <SpotlightClose
                onClick={handleDismiss}
                data-testid="menu-coachmark-welcome-close"
              />
            </SpotlightHeader>
            <SpotlightBody>
              <SpotlightImage>
                <video
                  src="/videos/menu-coachmark-welcome.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label={t('menuCoachmark.welcomeVideoAriaLabel')}
                />
              </SpotlightImage>
            </SpotlightBody>
            <SpotlightDescription>
              {t('menuCoachmark.welcomeDescription')}
            </SpotlightDescription>
            <SpotlightFooter>
              <div className="flex w-full justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleShowMe}
                  className="bg-surface text-text-primary hover:bg-background-secondary"
                  data-testid="menu-coachmark-show-me"
                >
                  {t('menuCoachmark.showMe')}
                </Button>
              </div>
            </SpotlightFooter>
          </SpotlightContent>
        </Spotlight>
      )}
    </div>
  );
};
